import { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { importMaterials, validateMaterialsImport } from "../api/materials";

const FIXED_HEADERS = {
  material_code: ["malzeme kodu", "kod"],
  material_name: ["malzeme adi", "malzeme adı", "ad"],
  material_type: ["tip"],
  unit: ["birim"],
};

function normalizeHeaderKey(x) {
  return String(x || "")
    .trim()
    .toLocaleLowerCase("tr")
    .replace(/ı/g, "i")
    .replace(/ş/g, "s")
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c")
    .replace(/\s+/g, " ");
}

function findHeaderIndex(headers, aliases) {
  const norm = headers.map(normalizeHeaderKey);
  for (const a of aliases) {
    const idx = norm.indexOf(normalizeHeaderKey(a));
    if (idx >= 0) return idx;
  }
  return -1;
}

export default function MaterialsImportModal({ onClose, onImported }) {
  const [fileName, setFileName] = useState("");
  const [parseError, setParseError] = useState("");
  const [rows, setRows] = useState([]);

  const [validating, setValidating] = useState(false);
  const [validated, setValidated] = useState(null); // { resolved, errors }

  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [result, setResult] = useState(null);

  const errorCount = useMemo(() => {
    if (!validated) return 0;
    return (validated.errors || []).length;
  }, [validated]);

  const canSubmit = !!rows.length && !validating && !saving && errorCount === 0 && !!validated;

  async function handlePickFile(file) {
    setParseError("");
    setSubmitError("");
    setResult(null);
    setRows([]);
    setValidated(null);
    setFileName(file?.name || "");
    if (!file) return;

    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array", cellDates: true });
      const sheetName = wb.SheetNames?.[0];
      if (!sheetName) throw new Error("Excel sayfası bulunamadı");
      const ws = wb.Sheets[sheetName];

      const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
      if (!Array.isArray(aoa) || aoa.length < 2) {
        throw new Error("Excel boş veya başlık satırı eksik");
      }

      const headerRow = (aoa[0] || []).map((x) => String(x || "").trim());
      const fixed = {
        material_code: findHeaderIndex(headerRow, FIXED_HEADERS.material_code),
        material_name: findHeaderIndex(headerRow, FIXED_HEADERS.material_name),
        material_type: findHeaderIndex(headerRow, FIXED_HEADERS.material_type),
        unit: findHeaderIndex(headerRow, FIXED_HEADERS.unit),
      };

      const missingFixed = Object.entries(fixed)
        .filter(([, idx]) => idx < 0)
        .map(([k]) => k);
      if (missingFixed.length) {
        throw new Error(`Eksik zorunlu sütun(lar): ${missingFixed.join(", ")}`);
      }

      // Varyant kolonları: sabit olmayan her kolon bir varyant key/label
      const fixedIdxSet = new Set(Object.values(fixed));
      const variantCols = [];
      for (let i = 0; i < headerRow.length; i += 1) {
        if (fixedIdxSet.has(i)) continue;
        const h = headerRow[i];
        if (!h) continue;
        variantCols.push({ idx: i, header: h });
      }

      const parsed = [];
      for (let i = 1; i < aoa.length; i += 1) {
        const r = aoa[i] || [];
        const code = String(r[fixed.material_code] || "").trim();
        const name = String(r[fixed.material_name] || "").trim();
        if (!code && !name) continue;

        const variants = {};
        for (const col of variantCols) {
          const raw = r[col.idx];
          const val = raw === null || raw === undefined ? "" : String(raw).trim();
          if (val) variants[col.header] = val;
        }

        parsed.push({
          material_code: code,
          material_name: name,
          material_type: String(r[fixed.material_type] || "").trim(),
          unit: String(r[fixed.unit] || "").trim(),
          variants,
        });
      }

      if (!parsed.length) {
        setParseError("Dosyada veri satırı bulunamadı.");
        return;
      }

      setRows(parsed);

      // Otomatik validate et
      setValidating(true);
      try {
        const res = await validateMaterialsImport(parsed);
        setValidated(res);
      } catch (err) {
        console.error("VALIDATE ERROR", err);
        setParseError(
          err?.response?.data?.error?.message || err?.message || "Doğrulama başarısız"
        );
      } finally {
        setValidating(false);
      }
    } catch (err) {
      console.error("EXCEL PARSE ERROR", err);
      setParseError(err?.message || "Excel okunamadı");
    }
  }

  async function submit() {
    setSubmitError("");
    setResult(null);

    if (!rows.length) return;
    if (errorCount > 0) {
      setSubmitError("Hatalı satırlar var. Lütfen düzeltin.");
      return;
    }

    setSaving(true);
    try {
      const res = await importMaterials(rows);
      setResult(res);
      await onImported?.(res);
    } catch (err) {
      console.error("MATERIALS IMPORT ERROR", err);
      const status = err?.response?.status;
      const dataObj = err?.response?.data;
      const message =
        dataObj?.error?.message ||
        dataObj?.message ||
        (typeof dataObj === "string" ? dataObj : null) ||
        err?.message ||
        null;
      setSubmitError(`İçe aktarılamadı${status ? ` (HTTP ${status})` : ""}${message ? `: ${message}` : ""}`);
    } finally {
      setSaving(false);
    }
  }

  const rowErrorMap = useMemo(() => {
    const m = new Map();
    for (const e of validated?.errors || []) {
      m.set(Number(e.row_index), e.errors);
    }
    return m;
  }, [validated]);

  return (
    <Dialog open onClose={saving ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          Malzeme Excel İçe Aktar
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Dosyayı seçin; malzemeler ve varyant şablonları otomatik oluşturulur/güncellenir.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {parseError ? <Alert severity="error">{parseError}</Alert> : null}
          {submitError ? <Alert severity="error">{submitError}</Alert> : null}

          {result ? (
            <Alert severity={result?.errors?.length ? "warning" : "success"}>
              Yeni malzeme: <b>{result.materials_created || 0}</b>, güncellenen: <b>{result.materials_updated || 0}</b>
              <br />
              Yeni varyant atama: <b>{result.variants_inserted || 0}</b>, güncellenen: <b>{result.variants_updated || 0}</b>
              {result?.errors?.length ? (
                <>
                  <br />
                  Hata: <b>{result.errors.length}</b>
                  <ul style={{ margin: "4px 0 0 16px" }}>
                    {result.errors.map((e, i) => (
                      <li key={i}>
                        <b>{e.material_code}</b>: {e.message}
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
            </Alert>
          ) : null}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
            <Button variant="outlined" component="label" disabled={saving}>
              Dosya Seç (.xlsx)
              <input
                type="file"
                hidden
                accept=".xlsx,.xls,.csv"
                onChange={(e) => handlePickFile(e.target.files?.[0] || null)}
              />
            </Button>
            <Typography variant="body2" color="text.secondary">
              {fileName ? `Seçili: ${fileName}` : "Henüz dosya seçilmedi"}
              {validating ? " (doğrulanıyor...)" : ""}
            </Typography>
          </Stack>

          {rows.length ? (
            <Box sx={{ overflowX: "auto" }}>
              <Typography
                variant="body2"
                color={errorCount > 0 ? "error" : "text.secondary"}
              >
                Satır: <b>{rows.length}</b> — Hata: <b>{errorCount}</b>
              </Typography>
              <Table size="small" sx={{ mt: 1 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Kod</TableCell>
                    <TableCell>Ad</TableCell>
                    <TableCell>Tip</TableCell>
                    <TableCell>Birim</TableCell>
                    <TableCell>Varyantlar</TableCell>
                    <TableCell>Durum</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r, idx) => {
                    const errs = rowErrorMap.get(idx);
                    return (
                      <TableRow key={idx} hover>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>{r.material_code}</TableCell>
                        <TableCell>{r.material_name}</TableCell>
                        <TableCell>{r.material_type}</TableCell>
                        <TableCell>{r.unit}</TableCell>
                        <TableCell sx={{ maxWidth: 360 }}>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {Object.entries(r.variants || {}).map(([k, v]) => (
                              <Chip
                                key={k}
                                size="small"
                                label={`${k}: ${v}`}
                                color={String(v).toLocaleLowerCase("tr") === "z" ? "primary" : "default"}
                                variant="outlined"
                              />
                            ))}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          {errs && errs.length ? (
                            <Typography variant="caption" color="error">
                              {errs.join(", ")}
                            </Typography>
                          ) : validated ? (
                            <Typography variant="caption" color="success.main">
                              OK
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>
          Kapat
        </Button>
        <Button onClick={submit} variant="contained" disabled={!canSubmit}>
          {saving ? "Oluşturuluyor..." : "Oluştur"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
